import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faGraduationCap, faCheck} from "@fortawesome/free-solid-svg-icons";
import {useResourcePersonSubmissions} from "../store/resourcePerson/resourcePersonStore";
import ResourcePersonShell from "../components/resourcePerson/ResourcePersonShell";
import EmptyState from "../components/learner/EmptyState";
import Toast from "../components/Toast";
import {shortDate} from "../lib/format";

/**
 * Resource-person submissions inbox (Stage 7):
 *
 *   GET   /api/resource-person/courses/:id/submissions
 *   PATCH /api/resource-person/courses/:id/submissions/:submissionId/grade
 *         { score, feedback? } — a pass completes the lesson and unlocks next.
 */
const ResourcePersonSubmissions = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const {
    submissions,
    submissionsLoading,
    submissionsError,
    gradeMutationLoading,
    gradeMutationError,
    fetchSubmissions,
    gradeSubmission,
  } = useResourcePersonSubmissions();

  const [toast, setToast] = useState(null);
  const [grades, setGrades] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchSubmissions(id);
  }, [id, fetchSubmissions]);

  const visible = (submissions || []).filter((submission) => {
    if (filter === "pending") return submission.status !== "GRADED" && submission.score == null;
    if (filter === "graded") return submission.status === "GRADED" || submission.score != null;
    return true;
  });

  const saveGrade = async (submission) => {
    const draft = grades[submission.id] || {};
    const score = Number(draft.score);
    if (Number.isNaN(score) || score < 0 || score > 100) {
      setToast({text: "Enter a score between 0 and 100.", ok: false});
      return;
    }
    const result = await gradeSubmission(id, submission.id, {
      score,
      ...(draft.feedback ? {feedback: draft.feedback} : {}),
    });
    if (!result.success) {
      setToast({text: result.error, ok: false});
      return;
    }
    setGrades((prev) => {
      const next = {...prev};
      delete next[submission.id];
      return next;
    });
    setToast({text: "Grade saved — a pass completes the lesson.", ok: true});
  };

  const inputClass =
    "w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-300";
  const ghostBtn =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60";

  return (
    <ResourcePersonShell
      title="Submissions"
      subtitle="Grade assignments, practicals and reports — a pass completes the lesson"
      icon={faGraduationCap}
      actions={
        <button type="button" className={ghostBtn} onClick={() => navigate(`/resource-person/content-library/course/${id}`)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Course
        </button>
      }
    >
      {toast ? <Toast message={toast.text} onClose={() => setToast(null)} isSuccess={toast.ok} /> : null}
      {submissionsError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{submissionsError}</p>
      ) : null}
      {gradeMutationError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{gradeMutationError}</p>
      ) : null}

      <div className="mb-4 flex gap-2">
        {["all", "pending", "graded"].map((option) => (
          <button
            key={option}
            type="button"
            className={ghostBtn}
            style={filter === option ? {background: "#1e3a8a", color: "#fff", borderColor: "#1e3a8a"} : undefined}
            onClick={() => setFilter(option)}
          >
            {option === "all" ? "All" : option === "pending" ? "Pending" : "Graded"}
          </button>
        ))}
      </div>

      {submissionsLoading && visible.length === 0 ? (
        <p className="text-sm text-slate-500">Loading submissions…</p>
      ) : visible.length === 0 ? (
        <EmptyState icon={faGraduationCap} title="No submissions" hint="Student work for this course appears here." />
      ) : (
        <ol className="m-0 grid list-none gap-3 p-0">
          {visible.map((submission) => {
            const draft = grades[submission.id] || {score: submission.score ?? "", feedback: submission.feedback || ""};
            const graded = submission.status === "GRADED" || submission.score != null;
            return (
              <li key={submission.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="m-0 text-sm font-semibold text-slate-900">
                      {submission.lessonTitle || submission.lessonId || "Lesson"}
                    </p>
                    <p className="m-0 mt-0.5 text-xs text-slate-500">
                      {submission.studentName || submission.studentEmail || submission.studentId || "Student"}
                      {(submission.submittedAt || submission.createdAt)
                        ? ` · ${shortDate(submission.submittedAt || submission.createdAt)}`
                        : ""}
                      {submission.type ? ` · ${submission.type}` : ""}
                    </p>
                  </div>
                  {graded ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      <FontAwesomeIcon icon={faCheck} /> {submission.score}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                      Pending
                    </span>
                  )}
                </div>
                {submission.text || submission.content ? (
                  <p className="mt-2 whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                    {submission.text || submission.content}
                  </p>
                ) : null}
                {submission.fileUrl || submission.resourceUrl ? (
                  <a
                    className="mt-2 inline-block text-sm font-medium text-blue-800"
                    href={submission.fileUrl || submission.resourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open attached file
                  </a>
                ) : null}
                <div className="mt-3 grid gap-2 sm:grid-cols-[120px_1fr_auto]">
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    max="100"
                    value={draft.score}
                    onChange={(e) => setGrades((prev) => ({...prev, [submission.id]: {...draft, score: e.target.value}}))}
                    placeholder="Score 0–100"
                  />
                  <input
                    className={inputClass}
                    value={draft.feedback}
                    onChange={(e) => setGrades((prev) => ({...prev, [submission.id]: {...draft, feedback: e.target.value}}))}
                    placeholder="Feedback (optional)"
                  />
                  <button type="button" className={ghostBtn} disabled={gradeMutationLoading} onClick={() => saveGrade(submission)}>
                    Save grade
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </ResourcePersonShell>
  );
};

export default ResourcePersonSubmissions;
